/**
 * Centralized LandlordOps → Discord activity pipeline (via POST /api/public/activity).
 *
 * - Non-blocking: work is scheduled with queueMicrotask + fetch (fire-and-forget).
 * - Enriches payloads with session id, device, viewport, URLs, navigation timing.
 *
 * Mixpanel uses `trackEvent` from helpers/mixpanel - this module exports
 * `trackActivityEvent` to avoid naming collisions.
 */

import { getAuth } from "firebase/auth";
import {
  getActivitySessionId,
  getDeviceType,
  getViewportSummary,
} from "./activityContext";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

const BATCH_IDLE_MS = 90_000;

let _pageViewBuffer = [];
let _idleTimer = null;

let _routeEnterTime = typeof Date !== "undefined" ? Date.now() : 0;
let _routeKey = "";

/**
 * Call on each SPA navigation before enqueueing page_view. Updates internal
 * clock and returns dwell time on the *previous* route.
 */
export function navigationTick(pathnameAndSearch) {
  const now = Date.now();
  const prevKey = _routeKey;
  const dwellMs =
    prevKey && prevKey !== pathnameAndSearch ? now - _routeEnterTime : undefined;
  _routeEnterTime = now;
  _routeKey = pathnameAndSearch;
  return {
    previousPath: prevKey || undefined,
    timeOnPreviousPageMs: dwellMs,
  };
}

function buildRichPayload(event, metadata = {}) {
  const user = getAuth().currentUser;
  const href =
    typeof window !== "undefined" ? window.location.href : undefined;
  const path =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : undefined;

  return {
    event,
    loggedIn: Boolean(user),
    userId: user?.uid ?? undefined,
    userEmail: user?.email ?? undefined,
    page: path,
    currentUrl: href,
    referrer:
      typeof document !== "undefined"
        ? document.referrer || undefined
        : undefined,
    referralUrl:
      typeof document !== "undefined"
        ? document.referrer || undefined
        : undefined,
    sessionId: getActivitySessionId(),
    deviceType: getDeviceType(),
    viewport: getViewportSummary(),
    clientTimestamp: new Date().toISOString(),
    metadata:
      metadata && typeof metadata === "object" && Object.keys(metadata).length > 0
        ? metadata
        : undefined,
  };
}

function schedulePost(body) {
  try {
    queueMicrotask(() => {
      fetch(`${API_BASE}/public/activity`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    });
  } catch {
    fetch(`${API_BASE}/public/activity`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  }
}

function _resetIdleTimer() {
  if (_idleTimer) clearTimeout(_idleTimer);
  _idleTimer = setTimeout(_flushPageViews, BATCH_IDLE_MS);
}

function _flushPageViews() {
  if (_idleTimer) {
    clearTimeout(_idleTimer);
    _idleTimer = null;
  }

  const batch = _pageViewBuffer.slice();
  _pageViewBuffer = [];

  if (batch.length === 0) return;

  const dwellSummary = batch
    .map((v) => {
      if (v.timeOnPreviousPageMs == null) return null;
      return `${v.previousPath || "?"} → ${v.path}: ${Math.round(
        v.timeOnPreviousPageMs / 1000
      )}s`;
    })
    .filter(Boolean);

  const body = {
    ...buildRichPayload("page_views"),
    page: batch[0]?.path,
    metadata: {
      pages: batch.map((v) => v.path),
      count: batch.length,
      duration_s:
        batch.length > 1
          ? Math.round((batch[batch.length - 1].ts - batch[0].ts) / 1000)
          : 0,
      dwell_segments: dwellSummary.slice(0, 12),
    },
  };

  schedulePost(body);
}

if (typeof window !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") _flushPageViews();
  });
  window.addEventListener("pagehide", _flushPageViews);
  window.addEventListener("beforeunload", _flushPageViews);
}

/**
 * Primary API: send a single activity event (Discord embed via backend).
 *
 * @param {string} event - machine name, e.g. ui_interaction, error_404, demo_message
 * @param {Record<string, unknown>} [metadata]
 */
export function trackActivityEvent(event, metadata = {}) {
  try {
    if (event === "page_view") {
      const nav = navigationTick(
        window.location.pathname + window.location.search
      );
      _pageViewBuffer.push({
        path: window.location.pathname + window.location.search,
        ts: Date.now(),
        previousPath: nav.previousPath,
        timeOnPreviousPageMs: nav.timeOnPreviousPageMs,
      });
      _resetIdleTimer();
      return;
    }

    const base = buildRichPayload(event, metadata);
    schedulePost(base);
  } catch {
    /* never throw */
  }
}

let _listenersInstalled = false;

function inferClickIntent(el) {
  if (!(el instanceof Element)) return null;
  const explicit = el.closest("[data-activity]")?.getAttribute("data-activity");
  if (explicit) return explicit;

  const link = el.closest("a[href]");
  if (link) {
    const href = (link.getAttribute("href") || "").toLowerCase();
    if (
      href.includes("/signup") ||
      href.includes("signup") ||
      href.includes("plan=free")
    )
      return "signup";
    if (href.includes("pricing") || href.includes("#pricing"))
      return "pricing";
    if (href.includes("/login")) return "login";
  }

  const btn = el.closest("button,[role='button']");
  const root = btn || el;
  const text = (root.textContent || "").trim().replace(/\s+/g, " ");
  const t = text.toLowerCase();
  if (/sign\s*up|get started|start free|create account|register/.test(t))
    return "signup";
  if (/pricing|see plans|view plans|plans/.test(t)) return "pricing";
  if (/log\s*in|sign\s*in/.test(t)) return "login";
  return null;
}

function textPreview(el, max = 80) {
  try {
    const t = (el.textContent || "").trim().replace(/\s+/g, " ");
    return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
  } catch {
    return "";
  }
}

/**
 * Messages from embedded Chromium hosts (CefSharp, some link scanners /
 * enterprise "safe" browsers). Not thrown by same-origin app code; reporting
 * them floods activity feeds.
 */
function isEmbeddedHostRuntimeNoise(message) {
  if (!message || typeof message !== "string") return false;
  return /Object Not Found Matching Id:\s*\d+,\s*MethodName:\s*[\w.]+,\s*ParamCount:\s*\d+/i.test(
    message
  );
}

/**
 * Best-effort serialization for Discord / debugging (not for end users).
 * Handles cross-realm Error objects where instanceof Error may be false.
 */
function serializeClientReason(reason) {
  const out = {
    reasonType: "unknown",
    reasonName: undefined,
    primaryMessage: "",
    stack: undefined,
    reasonSnapshot: undefined,
  };

  if (reason == null) {
    out.reasonType = "nullish";
    out.primaryMessage = String(reason);
    return out;
  }

  if (reason instanceof Error) {
    out.reasonType = "Error";
    out.reasonName = reason.name;
    out.primaryMessage = reason.message || String(reason);
    if (reason.stack) out.stack = String(reason.stack).slice(0, 1400);
    return out;
  }

  if (typeof reason === "string") {
    out.reasonType = "string";
    out.primaryMessage = reason;
    return out;
  }

  if (typeof reason === "object") {
    const msg = reason.message;
    if (typeof msg === "string") {
      out.reasonType = "object_with_message";
      out.primaryMessage = msg;
      out.reasonName = reason.name != null ? String(reason.name) : undefined;
      if (typeof reason.stack === "string") {
        out.stack = reason.stack.slice(0, 1400);
      }
      return out;
    }
    out.reasonType = "object";
    try {
      out.reasonSnapshot = JSON.stringify(reason).slice(0, 500);
    } catch {
      out.reasonSnapshot = String(reason).slice(0, 500);
    }
    out.primaryMessage =
      out.reasonSnapshot || "[object with no .message]";
    return out;
  }

  out.reasonType = typeof reason;
  out.primaryMessage = String(reason).slice(0, 400);
  return out;
}

export function installGlobalActivityListeners() {
  if (typeof window === "undefined" || _listenersInstalled) return;
  _listenersInstalled = true;

  document.addEventListener(
    "click",
    (e) => {
      const intent = inferClickIntent(e.target);
      if (!intent) return;
      const el = e.target instanceof Element ? e.target : null;
      trackActivityEvent("ui_interaction", {
        kind: "click",
        intent,
        tag: el?.tagName,
        preview: el ? textPreview(el.closest("a,button,[role='button']") || el) : "",
      });
    },
    true
  );

  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      const id = form instanceof HTMLFormElement ? form.id || "" : "";
      const name = form instanceof HTMLFormElement ? form.name || "" : "";
      trackActivityEvent("form_submit", {
        formId: id || undefined,
        formName: name || undefined,
        action:
          form instanceof HTMLFormElement
            ? (form.getAttribute("action") || "").slice(0, 200)
            : undefined,
      });
    },
    true
  );

  window.addEventListener("error", (e) => {
    const msg = e.message || "error";
    if (/ResizeObserver|Loading chunk \d+ failed/i.test(msg)) return;
    const err = e.error;
    const stack =
      err && typeof err === "object" && typeof err.stack === "string"
        ? String(err.stack).slice(0, 1400)
        : undefined;
    const errName =
      err && typeof err === "object" && err.name != null
        ? String(err.name).slice(0, 120)
        : undefined;
    trackActivityEvent("client_error", {
      message: String(msg).slice(0, 400),
      source: e.filename ? String(e.filename).slice(0, 200) : undefined,
      lineno: e.lineno,
      colno: e.colno,
      reasonType: err ? (err instanceof Error ? "Error" : typeof err) : undefined,
      reasonName: errName,
      stack,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const ser = serializeClientReason(e.reason);
    if (isEmbeddedHostRuntimeNoise(ser.primaryMessage)) return;
    trackActivityEvent("client_error", {
      message: `Unhandled rejection: ${ser.primaryMessage}`.slice(0, 450),
      source: "unhandledrejection",
      reasonType: ser.reasonType,
      reasonName: ser.reasonName,
      stack: ser.stack,
      reasonSnapshot: ser.reasonSnapshot,
    });
  });
}
